// frontend/src/utils/trackStyleView.js
// Utility for tracking style views across the application

import React from "react";
import { getIdentityHeaders } from "../lib/identity";
import { API_BASE } from "../lib/api";

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

/**
 * Track when a style is viewed
 * @param {string} styleId - The style ID to track
 * @param {string} context - Where the style was viewed ("search" | "browse" | "gallery" | "direct")
 * @returns {Promise<void>}
 */
export async function trackStyleView(styleId, context = "gallery") {
  // Don't track if no styleId
  if (!styleId || styleId === "default") return;

  try {
    await fetch(api("/api/analytics/style-view"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getIdentityHeaders(),
      },
      body: JSON.stringify({
        styleId: String(styleId).toLowerCase().trim(),
        context: context,
      }),
    });
  } catch (err) {
    // Silently fail - tracking is non-critical
    // Don't break user experience if analytics fail
    console.debug("Style view tracking failed (non-critical):", err);
  }
}

/**
 * Track multiple style views at once
 * @param {Array<{styleId: string, context: string}>} views - Array of styles to track
 * @returns {Promise<void>}
 */
export async function trackBulkStyleViews(views) {
  if (!views || views.length === 0) return;

  // Track each view (could be optimized with a bulk endpoint if needed)
  const promises = views.map(view => 
    trackStyleView(view.styleId, view.context)
  );

  try {
    await Promise.allSettled(promises);
  } catch (err) {
    console.debug("Bulk view tracking failed (non-critical):", err);
  }
}

/**
 * React hook for tracking style views with debouncing
 * @param {string} styleId - The style ID to track
 * @param {string} context - Where the style was viewed
 * @param {number} delay - Debounce delay in ms (default: 1000)
 */
export function useStyleViewTracking(styleId, context = "gallery", delay = 1000) {
  const [hasTracked, setHasTracked] = React.useState(false);

  React.useEffect(() => {
    if (!styleId || styleId === "default" || hasTracked) return;

    // Debounce - only track if user stays on page for specified delay
    const timer = setTimeout(() => {
      trackStyleView(styleId, context);
      setHasTracked(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [styleId, context, delay, hasTracked]);
}

export default trackStyleView;